import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { toast } from "sonner";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Pluralize a word based on count.
 * pluralize(1, 'school') → 'school'
 * pluralize(3, 'school') → 'schools'
 * pluralize(2, 'category', 'categories') → 'categories'
 */
export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural ?? singular + 's');
}

/**
 * The user-facing sentence for a thrown value, or the caller's fallback when there isn't one.
 *
 * The old rule was `err instanceof Error ? err.message : fallback`, and the trap is that a
 * SyntaxError IS an Error — so the fallback could never fire for exactly the errors that most
 * needed it. A director uploading a large handbook was shown
 * `Unexpected token 'R', "Request En" is not valid JSON`, which is our bug quoted back at her.
 *
 * So: an error the SERVER wrote is worth showing; an error the RUNTIME wrote is not.
 */
export function userFacingMessage(err: unknown, fallback: string): string {
  if (!(err instanceof Error)) return fallback;
  // Thrown by the runtime, never by us: a failed JSON.parse, a dead fetch, a bad property access.
  if (err instanceof SyntaxError || err instanceof TypeError) return fallback;
  const message = err.message?.trim();
  if (!message) return fallback;
  // Belt and braces for the same shapes arriving as a plain Error across a bundler boundary.
  if (/is not valid JSON|Unexpected token|Failed to fetch|NetworkError|Load failed|<!DOCTYPE/i.test(message)) {
    return fallback;
  }
  return message;
}

/**
 * Show a toast error from an unknown catch value. Prefer a real message from the server, and fall
 * back rather than showing the director a parser or network error she cannot act on.
 */
export function toastError(err: unknown, fallback = 'Something went wrong') {
  toast.error(userFacingMessage(err, fallback));
}

// Round to 1 decimal, killing binary-float noise (8.3 - 2 → 6.3, not 6.300000000000001).
export function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
